import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, EMPTY, finalize, Observable, throwError, TimeoutError } from 'rxjs';
import { catchError, filter, switchMap, take, timeout } from 'rxjs/operators';
import { AuthService } from './auth.service';

// Singleton state for token refresh logic
const refreshState = {
  isRefreshing: false,
  refreshTokenSubject: new BehaviorSubject<string | null>(null),
};

/**
 *
 * @important previous interceptors where error messages bubble up during refresh token failure
 * caused unwanted error alerts in the UI. This implementation swallows those errors
 * by returning EMPTY observable on refresh failure, ensuring a silent logout without
 * disturbing the user experience.
 */
// Create the interceptor function
// export const authErrorsInterceptor: HttpInterceptorFn = (request: HttpRequest<any>, next: HttpHandlerFn) => {
//   const authService = inject(AuthService);
//   const addToken = (req: HttpRequest<any>, token: string): HttpRequest<any> => {
//     return req.clone({
//       setHeaders: {
//         Authorization: `Bearer ${token}`,
//       },
//     });
//   };

//   const handle401Error = (req: HttpRequest<any>): Observable<any> => {
//     const refreshToken = localStorage.getItem('refresh_token');
//     console.log('Trying to reconnect, Refresh Token:', refreshToken);
//     if (!refreshToken || refreshToken === 'undefined') {
//       authService.logout();
//       return EMPTY; // Return empty instead of error
//     }

//     if (!refreshState.isRefreshing) {
//       refreshState.isRefreshing = true;
//       refreshState.refreshTokenSubject.next(null);

//       return authService.sendTokenRefreshRequest(refreshToken).pipe(
//         switchMap((res) => {
//           refreshState.isRefreshing = false;
//           refreshState.refreshTokenSubject.next(res.access_token);
//           return next(addToken(req, res.access_token));
//         })
//       );
//     } else {
//       return refreshState.refreshTokenSubject.pipe(
//         filter((token) => token !== null),
//         take(1),
//         switchMap((jwt) => {
//           if (jwt) {
//             return next(addToken(req, jwt));
//           }
//           return EMPTY; // Return empty instead of error
//         })
//       );
//     }
//   };

//   // Main interceptor logic
//   return next(request).pipe(
//     timeout(240000),
//     catchError((error: HttpErrorResponse) => {
//       if (error.status === 401 && !request.url?.includes('auth/login')) {
//         return handle401Error(request);
//       }
//       if (error.error instanceof ErrorEvent) {
//         // client-side error
//       } else if (error instanceof TimeoutError) {
//         error = { ...error, error: {}, status: 408 } as HttpErrorResponse;
//         error.error.message = 'Timeout has occurred';
//         error.error.errorMessage = 'Timeout has occurred';
//       } else if (error.error instanceof ProgressEvent) {
//         error = { ...error, error: error.error, status: 503 } as HttpErrorResponse;
//         error.error.message = 'Server Unavailable';
//         error.error.errorMessage = 'Server Unavailable';
//       }

//       return throwError(() => error);
//     })
//   );
// };

/**
 *
 *@experimental solution to handle auth errors globally with silent logout on refresh failure
 */
export const authErrorsInterceptor: HttpInterceptorFn = (request: HttpRequest<any>, next: HttpHandlerFn) => {
  const injector = inject(Injector);

  const addToken = (req: HttpRequest<any>, token: string): HttpRequest<any> => {
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  };

  const handle401Error = (req: HttpRequest<any>): Observable<HttpEvent<any>> => {
    // Change return type to HttpEvent<any> for retry
    const authService = injector.get(AuthService); // Lazy resolution to break circular dependency
    const refreshToken = localStorage.getItem('refresh_token');

    if (!refreshToken || refreshToken === 'undefined') {
      // authService.logout();
      authService.inAppLogout();
      return EMPTY; // silent, no error to subscriber
    }

    if (!refreshState.isRefreshing) {
      refreshState.isRefreshing = true;
      refreshState.refreshTokenSubject.next(null);

      return authService.sendTokenRefreshRequest(refreshToken).pipe(
        switchMap((res) => {
          refreshState.isRefreshing = false;
          refreshState.refreshTokenSubject.next(res.access_token);

          // Retry the original request with new token
          return next(addToken(req, res.access_token));
        }),
        catchError((refreshErr) => {
          refreshState.isRefreshing = false;
          refreshState.refreshTokenSubject.next(null); // reset so waiting requests don't hang forever
          authService.inAppLogout();

          // IMPORTANT: Return EMPTY instead of throwError → swallows the error
          // Original subscribers get complete() without error → no alert
          return EMPTY;
        })
      );
    } else {
      // Wait for refresh to complete (or fail)
      return refreshState.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((jwt) => {
          if (jwt) {
            return next(addToken(req, jwt));
          }
          // If refresh failed, subject emitted null → we reach here
          // But since refresh pipe already logged out and returned EMPTY to first caller,
          // we can safely return EMPTY here too
          return EMPTY;
        })
        // Optional: add defaultIfEmpty(EMPTY) or similar if needed
      );
    }
  };

  return next(request).pipe(
    timeout(240000),
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && !request.url?.includes('auth/login') && !request.url?.includes('profile/logout')) {
        return handle401Error(request);
      }

      if (error.error instanceof ErrorEvent) {
        // client-side error
      } else if (error instanceof TimeoutError) {
        error = { ...error, error: {}, status: 408 } as HttpErrorResponse;
        error.error.message = 'Timeout has occurred';
        error.error.errorMessage = 'Timeout has occurred';
      } else if (error.error instanceof ProgressEvent) {
        error = { ...error, error: error.error, status: 503 } as HttpErrorResponse;
        error.error.message = 'Server Unavailable';
        error.error.errorMessage = 'Server Unavailable';
      }

      // For non-401 errors → still throw so components can show specific messages
      return throwError(() => error);
    })
  );
};
